/* =============================================================
   UGC Product Videos
   - Lazy source activation + muted autoplay when near viewport
   - Pause when off-screen
   - Click card -> open reusable modal (sound, controls, autoplay)
   - prefers-reduced-motion: no autoplay, poster shown until click
   - One IntersectionObserver per gallery instance; no per-video
     global listeners; safe with multiple section instances.
   ============================================================= */
(function () {
  'use strict';

  if (window.customElements && customElements.get('ds-ugc-videos')) return;

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  function parseSources(card) {
    try {
      return JSON.parse(card.getAttribute('data-sources') || '[]');
    } catch (e) {
      return [];
    }
  }

  function applySources(video, sources) {
    if (video.dataset.ugcLoaded === 'true' || !sources.length) return;
    sources.forEach(function (s) {
      if (!s || !s.src) return;
      var el = document.createElement('source');
      el.src = s.src;
      if (s.type) el.type = s.type;
      video.appendChild(el);
    });
    video.dataset.ugcLoaded = 'true';
    // Only call load() after the sources have been attached.
    try { video.load(); } catch (e) {}
  }

  function safePlay(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { /* autoplay rejected: leave poster visible */ });
    }
  }

  var DsUgcVideos = function () {};

  try {
    DsUgcVideos = class extends HTMLElement {
      constructor() {
        super();
        this._cards = [];
        this._observer = null;
        this._modal = null;
        this._modalVideo = null;
        this._lastFocused = null;
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        if (this._initialised) return;
        this._initialised = true;

        this.autoplay = this.getAttribute('data-autoplay') === 'true';
        this.loopPreview = this.getAttribute('data-loop') !== 'false';
        this.useModal = this.getAttribute('data-modal') !== 'false';

        this._cards = Array.prototype.slice.call(
          this.querySelectorAll('[data-ugc-card]')
        );
        this._modal = this.querySelector('[data-ugc-modal]');
        this._modalVideo = this.querySelector('[data-ugc-modal-video]');

        // Move the modal to <body> so it can never be trapped by an ancestor
        // that establishes a containing block / stacking context (the product
        // info wrapper gets a CSS transform from the theme's reveal-on-scroll
        // animation, which otherwise breaks position:fixed and z-index).
        if (this._modal) {
          var ownerId = this.id || '';
          var orphan = document.body.querySelector(
            '[data-ugc-modal][data-ugc-owner="' + ownerId + '"]'
          );
          if (orphan && orphan !== this._modal) orphan.parentNode.removeChild(orphan);
          if (ownerId) this._modal.setAttribute('data-ugc-owner', ownerId);
          this._modal.hidden = true;
          document.body.appendChild(this._modal);
        }

        this._setupPreviews();
        this._setupModal();
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        document.removeEventListener('keydown', this._onKeydown);
        // Never leave the page locked or an orphaned overlay behind.
        document.body.classList.remove('ds-ugc-modal-open');
        if (this._modal && this._modal.parentNode) {
          this._modal.parentNode.removeChild(this._modal);
        }
      }

      /* ---------- Lazy previews ---------- */
      _setupPreviews() {
        var self = this;

        this._cards.forEach(function (card) {
          var video = card.querySelector('.ds-ugc__video');
          if (video && !self.loopPreview) video.loop = false;
          else if (video) video.loop = true;
        });

        if (!('IntersectionObserver' in window)) {
          // Fallback: load + (optionally) play everything once.
          this._cards.forEach(function (card) {
            var video = card.querySelector('.ds-ugc__video');
            if (!video) return;
            applySources(video, parseSources(card));
            if (self.autoplay && !REDUCED_MOTION) safePlay(video);
          });
          return;
        }

        this._observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var video = entry.target.querySelector('.ds-ugc__video');
              if (!video) return;

              if (entry.isIntersecting) {
                applySources(video, parseSources(entry.target));
                if (self.autoplay && !REDUCED_MOTION) safePlay(video);
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { root: null, rootMargin: '200px 0px', threshold: 0.1 }
        );

        this._cards.forEach(function (card) {
          self._observer.observe(card);
        });
      }

      /* ---------- Modal ---------- */
      _setupModal() {
        var self = this;
        if (!this._modal || !this._modalVideo) return;

        this._cards.forEach(function (card) {
          card.addEventListener('click', function () {
            if (self.useModal) self._openModal(card);
          });
        });

        var closeBtn = this._modal.querySelector('[data-ugc-modal-close]');
        var backdrop = this._modal.querySelector('[data-ugc-modal-backdrop]');
        if (closeBtn) closeBtn.addEventListener('click', function () { self._closeModal(); });
        if (backdrop) backdrop.addEventListener('click', function () { self._closeModal(); });
      }

      _openModal(card) {
        if (!this._modal || !this._modalVideo) return;
        var sources = parseSources(card);
        if (!sources.length) return;

        this._lastFocused = card;

        // Reset and (re)load the single reusable modal video.
        var v = this._modalVideo;
        while (v.firstChild) v.removeChild(v.firstChild);
        v.removeAttribute('src');
        v.dataset.ugcLoaded = '';
        applySources(v, sources);

        // Carry the poster across for a smoother open.
        var preview = card.querySelector('.ds-ugc__video');
        if (preview && preview.getAttribute('poster')) {
          v.setAttribute('poster', preview.getAttribute('poster'));
        }

        this._modal.hidden = false;
        document.body.classList.add('ds-ugc-modal-open');
        document.addEventListener('keydown', this._onKeydown);

        var closeBtn = this._modal.querySelector('[data-ugc-modal-close]');
        if (closeBtn) closeBtn.focus();

        v.muted = false;
        v.currentTime = 0;
        safePlay(v);
      }

      _closeModal() {
        if (!this._modal || this._modal.hidden) return;
        var v = this._modalVideo;
        try { v.pause(); v.currentTime = 0; } catch (e) {}
        while (v.firstChild) v.removeChild(v.firstChild);
        v.removeAttribute('src');
        v.dataset.ugcLoaded = '';

        this._modal.hidden = true;
        document.body.classList.remove('ds-ugc-modal-open');
        document.removeEventListener('keydown', this._onKeydown);

        if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
          this._lastFocused.focus();
        }
        this._lastFocused = null;
      }

      _onKeydown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          this._closeModal();
          return;
        }
        // Minimal focus trap: keep focus on the close button.
        if (e.key === 'Tab') {
          var closeBtn = this._modal.querySelector('[data-ugc-modal-close]');
          if (closeBtn) {
            e.preventDefault();
            closeBtn.focus();
          }
        }
      }
    };
  } catch (e) {
    return;
  }

  customElements.define('ds-ugc-videos', DsUgcVideos);
})();
